import React, { useEffect, useRef, useState } from 'react';
import { useClientConfigs } from './ClientConfigContext';
import { buildPageViewEvent, getPageLoadLatency, mapGtmUserType, readSoaCookie, readUtmParams, resolveSoa, shouldFireGtm } from '../lib/gtm-events';
import { useAutoRedirectContext } from './AutoRedirectContext';

const MAX_RETRY_COUNT = 5;

export type IGeicoDtm = {
    application: string,
    UIVersion: string,
    viewName: string,
    language: string,
    product?: string,
    zip?: string,
    state?: string,
    crossSellType?: string,
    crossSellOffer?: string,
    serviceMilestone: Array<string>,
    launchpadId: string,
    soa: string,
    pageName?: string,
    previousPageName?: string,
    eventType?: string,
    pageLoadLatency?: number,
    timeOnPage?: number,
    userType?: string,
    isDomLoad?: boolean,
    utm_source?: string,
    utm_medium?: string,
    utm_campaign?: string,
    utm_term?: string,
    utm_content?: string,
    utm_id?: string,
    utm_soa?: string,
}

declare global {
    interface Window {
        geicoDtm: IGeicoDtm,
        _gtm: any | undefined;
    }
}

export class GeicoPageNames {
    static GuidedUnGuided: string= "Sales:Commercial:Quote:GuidedUnGuided";
    static Launchpad: string= "Sales:Commercial:Quote:Launchpad";
    static Questions: string= "Sales:Commercial:Quote:GuidedQuestions";
}

const defaultGeicoDtm: IGeicoDtm = {
    application: "y",
    UIVersion: "React",
    viewName: "",
    language: "dtm-en-us",
    product: "",
    zip: "",
    state: "",
    crossSellType: "",
    crossSellOffer: "",
    serviceMilestone:[],
    launchpadId: "",
    soa: ""
};

const trackWhenReady = (name: string, data?: Record<string, unknown>, retryCount: number = 1) => {
    if (retryCount >= MAX_RETRY_COUNT) {
        return;
    }

    if (window._gtm !== null && window._gtm !== undefined && typeof window._gtm.track === "function") {
        if (data) {
            window._gtm.track(name, data);
        } else {
            window._gtm.track(name);
        }
    }
    else {
        setTimeout(() => {
            trackWhenReady(name, data, ++retryCount);
        }, 2000);
    }
}

const sendGeicoDtm = (retryCount: number =1, submitForm: boolean) => {
    if (retryCount >= MAX_RETRY_COUNT) {
        return;
    }

    if (window._gtm !== null && window._gtm !== undefined && typeof window._gtm.track === "function") {
        if (submitForm) {
            window._gtm.track("redirectZip");
        }
        window._gtm.track("updateData");
    }
    else {
        setTimeout(() => {
            sendGeicoDtm(++retryCount, submitForm);
        }, 2000);
    }
}

interface IAnalyticsContext {
    geicoDtm: IGeicoDtm,
    setGeicoDtm: (geicoDtm: IGeicoDtm) => void,
    sendToAnalytics: (formSubmit: boolean) => void
}

const AnalyticsContext = React.createContext<IAnalyticsContext>({
    geicoDtm: defaultGeicoDtm,
    setGeicoDtm: (geicoDtm: IGeicoDtm): void => { },
    sendToAnalytics: (formSubmit: boolean): void => { }
});

type Props = {
       children: React.ReactNode;
};

const AnalyticsContextProvider: React.FC<Props> = ({children}) => {

    const [geicoDtmModel, setGeicoDtmModel] = useState<IGeicoDtm>(defaultGeicoDtm);
    const currentPageRef = useRef("");
    const previousPageNameRef = useRef("");
    const pageEnteredAtRef = useRef(typeof performance !== "undefined" ? performance.now() : 0);
    const firstPageLoadRef = useRef(true);

    const clientConfigs = useClientConfigs();
    const redirectContext = useAutoRedirectContext();

    const enrichDtm = (geicoDtm: IGeicoDtm): IGeicoDtm => {
        const utm = typeof window !== "undefined" ? readUtmParams(window.location.search) : {};
        const pageName = geicoDtm.viewName || window.geicoDtm?.viewName || "";
        const soa = resolveSoa(redirectContext.soa, readSoaCookie());

        return {
            ...geicoDtm,
            viewName: pageName,
            pageName,
            previousPageName: previousPageNameRef.current,
            language: geicoDtm.language,
            userType: mapGtmUserType(clientConfigs.userType),
            soa,
            ...utm,
        };
    };

    const firePageView = (dtm: IGeicoDtm, eventType: "PageLoad" | "PageExit", extras: {
        previousPageName: string,
        pageLoadLatency?: number,
        timeOnPage?: number,
        isDomLoad?: boolean,
    }) => {
        const payload = buildPageViewEvent(dtm, {
            eventType,
            previousPageName: extras.previousPageName,
            pageLoadLatency: extras.pageLoadLatency,
            timeOnPage: extras.timeOnPage,
            isDomLoad: extras.isDomLoad,
            utm: readUtmParams(window.location.search),
        });
        window.geicoDtm.eventType = eventType;
        if (extras.pageLoadLatency != null) window.geicoDtm.pageLoadLatency = extras.pageLoadLatency;
        if (extras.timeOnPage != null) window.geicoDtm.timeOnPage = extras.timeOnPage;
        if (extras.isDomLoad != null) window.geicoDtm.isDomLoad = extras.isDomLoad;
        trackWhenReady("PageView", payload);
    };

    const updateGeicoDtm = (geicoDtm: IGeicoDtm):void => {
        const updatedGeicoDtm = enrichDtm(geicoDtm);
        setGeicoDtmModel(updatedGeicoDtm);

        if (!shouldFireGtm(clientConfigs.userType)) {
            return;
        }

        if (!window.geicoDtm) {
            window.geicoDtm = { ...defaultGeicoDtm };
        }
        Object.assign(window.geicoDtm, updatedGeicoDtm);

        const nextPage = updatedGeicoDtm.viewName || "";
        const leavingPage = currentPageRef.current;
        if (nextPage && nextPage !== leavingPage) {
            if (leavingPage) {
                firePageView({ ...updatedGeicoDtm, viewName: leavingPage }, "PageExit", {
                    previousPageName: previousPageNameRef.current,
                    timeOnPage: Math.round(performance.now() - pageEnteredAtRef.current),
                });
            }
            const isDomLoad = firstPageLoadRef.current;
            firstPageLoadRef.current = false;
            firePageView(updatedGeicoDtm, "PageLoad", {
                previousPageName: leavingPage,
                pageLoadLatency: getPageLoadLatency(),
                isDomLoad,
            });
            previousPageNameRef.current = leavingPage;
            currentPageRef.current = nextPage;
            pageEnteredAtRef.current = performance.now();
            window.geicoDtm.previousPageName = leavingPage;
            window.geicoDtm.viewName = nextPage;
            window.geicoDtm.pageName = nextPage;
        }
    }

    useEffect(() => {
        if (!shouldFireGtm(clientConfigs.userType) || !clientConfigs.geicoAnalyticsUri) {
            return;
        }
        try {
            window._gtm = undefined;

            const gtmPreloadLink = document.createElement("link");
            gtmPreloadLink.rel = "preload";
            gtmPreloadLink.href = clientConfigs.geicoAnalyticsUri;
            gtmPreloadLink.as = "script";
            document.head.appendChild(gtmPreloadLink);

            const gtmScript = document.createElement("script");
            gtmScript.src = clientConfigs.geicoAnalyticsUri;
            gtmScript.async = true;
            gtmScript.id = "gtm-script";
            document.head.appendChild(gtmScript);

        }
        catch (error) {
            console.error("Unable to start Geico analytics");
        }
    }, [clientConfigs.userType, clientConfigs.geicoAnalyticsUri]);

    useEffect(() => {
        const onPageHide = () => {
            if (!shouldFireGtm(clientConfigs.userType) || !window.geicoDtm?.viewName) {
                return;
            }
            firePageView(window.geicoDtm, "PageExit", {
                previousPageName: previousPageNameRef.current,
                timeOnPage: Math.round(performance.now() - pageEnteredAtRef.current),
            });
        };
        window.addEventListener("pagehide", onPageHide);
        return () => window.removeEventListener("pagehide", onPageHide);
    }, [clientConfigs.userType]);

    const sentDtmToAnalytics = (formSubmit: boolean) => {
        if (!shouldFireGtm(clientConfigs.userType)) {
            return;
        }
        sendGeicoDtm(1, formSubmit);
    }

    return (
        <AnalyticsContext.Provider value={{ geicoDtm: geicoDtmModel, sendToAnalytics: sentDtmToAnalytics, setGeicoDtm: updateGeicoDtm }}>
            {children}
        </AnalyticsContext.Provider>
    );
}

const useAnalytics = (): IAnalyticsContext => {

    const context = React.useContext(AnalyticsContext);

    if (!context) {
        throw new Error(
            "useAnalytics must be used within a AnalyticsContextProvider"
        );
    }

    return context;
}

export { AnalyticsContextProvider, AnalyticsContext, useAnalytics };
